import { useMemo, useState, useEffect } from "react";
import type { ImageProps } from "./types";
import {
  isUrl,
  isSupportedFormat,
  toWebSrc,
  placeholderDataUri,
  isDataUri,
  isRemoteWithoutExt,
  getBorderRadius,
} from "./utils";

let shimmerStylesInjected = false;

function ensureShimmerStyles() {
  if (shimmerStylesInjected) {
    return;
  }
  if (typeof document === "undefined") {
    return;
  }
  const styleElement = document.createElement("style");
  styleElement.textContent = `
    @keyframes ui-image-shimmer {
      0% { background-position: 200% 0; }
      100% { background-position: -200% 0; }
    }
    .ui-image-shimmer {
      background-color: rgba(148,163,184,0.25);
      background-image: linear-gradient(
        270deg,
        rgba(15,23,42,0.04) 0%,
        rgba(15,23,42,0.12) 50%,
        rgba(15,23,42,0.04) 100%
      );
      background-repeat: no-repeat;
      background-size: 300% 100%;
      animation: ui-image-shimmer 1.1s ease-in-out infinite;
    }
    @media (prefers-reduced-motion: reduce) {
      .ui-image-shimmer {
        animation: none;
        background-position: 0 0;
      }
    }
    .ui-image-clickable {
      cursor: pointer;
      transition: opacity 0.2s;
    }
    .ui-image-clickable:active {
      opacity: 0.7 !important;
    }
  `;
  document.head.appendChild(styleElement);
  shimmerStylesInjected = true;
}

export function Image({
  source,
  resizeMode = "contain",
  accessibilityLabel,
  style,
  onLoad,
  onError,
  loadingIndicatorSource,
  lazy = true,
  fetchPriority = "auto",
  placeholder,
  fallback,
  disableLoadingUI,
  rounded,
  aspectRatio,
  overlay,
  onPress,
  disabled,
  decorative,
  borderColor,
  borderWidth,
}: ImageProps) {
  ensureShimmerStyles();
  const resolved = useMemo(() => toWebSrc(source), [source]);
  const [status, setStatus] = useState<"idle" | "loading" | "loaded" | "error">(
    "loading",
  );
  const initialFallback =
    typeof loadingIndicatorSource === "string"
      ? loadingIndicatorSource
      : loadingIndicatorSource && "src" in (loadingIndicatorSource as any)
        ? (loadingIndicatorSource as any).src
        : undefined;
  const [currentSrc, setCurrentSrc] = useState<string>(
    resolved.src || initialFallback || "",
  );

  useEffect(() => {
    const src = resolved.src || "";
    const isRemote = isUrl(src);
    const supported =
      isSupportedFormat(src) || isDataUri(src) || isRemoteWithoutExt(src);
    if (!src) {
      setStatus("error");
      setCurrentSrc(
        typeof loadingIndicatorSource === "string"
          ? loadingIndicatorSource
          : loadingIndicatorSource && "src" in (loadingIndicatorSource as any)
            ? (loadingIndicatorSource as any).src
            : placeholderDataUri(),
      );
      return;
    }
    if (!supported) {
      setStatus("error");
      setCurrentSrc(
        typeof loadingIndicatorSource === "string"
          ? loadingIndicatorSource
          : loadingIndicatorSource && "src" in (loadingIndicatorSource as any)
            ? (loadingIndicatorSource as any).src
            : placeholderDataUri(),
      );
      return;
    }
    if (
      isRemote &&
      !(src.startsWith("http://") || src.startsWith("https://"))
    ) {
      setStatus("error");
      setCurrentSrc(
        typeof loadingIndicatorSource === "string"
          ? loadingIndicatorSource
          : loadingIndicatorSource && "src" in (loadingIndicatorSource as any)
            ? (loadingIndicatorSource as any).src
            : placeholderDataUri(),
      );
      return;
    }
    setStatus("loading");
    setCurrentSrc(src);
  }, [resolved.src, loadingIndicatorSource]);

  const objectFit =
    resizeMode === "cover"
      ? "cover"
      : resizeMode === "contain"
        ? "contain"
        : resizeMode === "stretch"
          ? "fill"
          : resizeMode === "center"
            ? "none"
            : "contain";

  const radius = getBorderRadius(rounded);

  const baseStyle = (style || {}) as any;
  const {
    borderWidth: styleBorderWidth,
    borderColor: styleBorderColor,
    borderStyle: styleBorderStyle,
    ...imgStyleFromStyle
  } = baseStyle;

  const imgStyle: any = {
    objectFit,
    ...imgStyleFromStyle,
    opacity: status === "loaded" ? 1 : 0,
    aspectRatio,
    transition: "opacity 0.3s ease-in-out",
  };

  const handleLoad = () => {
    setStatus("loaded");
    if (onLoad) onLoad();
  };
  const handleError = () => {
    setStatus("error");
    if (loadingIndicatorSource) {
      const fb =
        typeof loadingIndicatorSource === "string"
          ? loadingIndicatorSource
          : "src" in (loadingIndicatorSource as any)
            ? (loadingIndicatorSource as any).src
            : placeholderDataUri();
      setCurrentSrc(fb);
    }
    if (onError) onError();
  };

  const commonProps: any = {
    alt: decorative ? "" : accessibilityLabel,
    role: decorative ? "presentation" : undefined,
    style: imgStyle,
    loading: lazy ? "lazy" : undefined,
    decoding: "async",
    fetchPriority: fetchPriority,
    onLoad: handleLoad,
    onError: handleError,
  };

  const containerStyle: any = {
    position: "relative",
    display: "inline-block",
    cursor: onPress && !disabled ? "pointer" : undefined,
    opacity: disabled ? 0.6 : 1,
    borderRadius: radius,
    overflow: "hidden",
    ...baseStyle,
    width: baseStyle.width,
    height: baseStyle.height,
    borderWidth: borderWidth ?? styleBorderWidth,
    borderColor: borderColor ?? styleBorderColor,
    borderStyle:
      (borderWidth ?? styleBorderWidth)
        ? styleBorderStyle || "solid"
        : styleBorderStyle,
  };

  // Remove width/height from imgStyle if it's on container to avoid conflict?
  // Actually, usually img { width: 100%; height: 100% } is good if container has size.
  // But existing usage passes style to img.
  // Let's keep style on img but ensure container fits.
  // If style has width/height, we put it on container and make img 100%.

  if (style?.width || style?.height) {
    imgStyle.width = "100%";
    imgStyle.height = "100%";
  }

  const handlePress = () => {
    if (onPress && !disabled) {
      onPress();
    }
  };

  if (status === "error" && fallback) {
    return (
      <div
        style={containerStyle}
        onClick={handlePress}
        role={onPress ? "button" : undefined}
        className={onPress && !disabled ? "ui-image-clickable" : undefined}
      >
        {fallback}
        {overlay && (
          <div
            style={{
              position: "absolute",
              inset: 0,
              backgroundColor: overlay,
              pointerEvents: "none",
            }}
          />
        )}
      </div>
    );
  }

  return (
    <div
      style={containerStyle}
      onClick={handlePress}
      role={onPress ? "button" : undefined}
      className={onPress && !disabled ? "ui-image-clickable" : undefined}
    >
      {status === "loading" && !disableLoadingUI && (
        <div style={{ position: "absolute", inset: 0, zIndex: 1 }}>
          {placeholder ? (
            placeholder
          ) : (
            <div
              className="ui-image-shimmer"
              style={{
                width: "100%",
                height: "100%",
                backgroundColor: (style?.backgroundColor as any) || "#e0e0e0",
              }}
            />
          )}
        </div>
      )}
      {status !== "error" && (
        <img
          {...commonProps}
          src={currentSrc}
          srcSet={
            "srcSet" in (resolved as any) ? (resolved as any).srcSet : undefined
          }
          sizes={
            "sizes" in (resolved as any) ? (resolved as any).sizes : undefined
          }
        />
      )}
      {overlay && (
        <div
          style={{
            position: "absolute",
            inset: 0,
            backgroundColor: overlay,
            pointerEvents: "none",
          }}
        />
      )}
    </div>
  );
}
